if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.ofkPageActive = true;
        this.ofkOnPageHide = () => {
          this.ofkPageActive = false;
          this.ofkInvalidateAttempt();
        };
        this.ofkOnPageShow = () => {
          this.ofkPageActive = true;
          if (this.ofkNeedsReview) this.ofkShowRecovery();
        };
      }

      connectedCallback() {
        this.ofkPageActive = true;
        window.addEventListener('pagehide', this.ofkOnPageHide);
        window.addEventListener('pageshow', this.ofkOnPageShow);
        if (this.ofkNeedsReview) this.ofkShowRecovery();
      }

      disconnectedCallback() {
        // QuickAddModal.hide intentionally removes this form before modalClosed.
        if (this.ofkAttempt?.confirmed && this.ofkAttempt.handoff) return;
        window.removeEventListener('pagehide', this.ofkOnPageHide);
        window.removeEventListener('pageshow', this.ofkOnPageShow);
        this.ofkInvalidateAttempt();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.ofkPageActive || this.ofkAttempt?.pending || this.ofkNeedsReview ||
            this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.ofkInvalidateAttempt();
        const attempt = { pending: true, sent: false, invalid: false, deferred: null, controller: null };
        this.ofkAttempt = attempt;
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        // The deadline owns headers AND body. Aborting cannot establish whether Shopify added the item.
        this.ofkRunAdd(attempt, evt);
      }

      async ofkRunAdd(attempt, evt) {
        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          const formData = new FormData(this.form);
          attempt.snapshot = this.ofkFormSnapshot();
          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity')) || 1;
          attempt.deferred = this.createCartLinesUpdateEvent(variantId, quantity);
          try {
            if (typeof AbortController !== 'undefined') {
              attempt.controller = new AbortController();
              config.signal = attempt.controller.signal;
            }
          } catch (_) { /* The timer remains available without an abort implementation. */ }

          const response = await new Promise((resolve, reject) => {
            let settled = false;
            const finish = (callback, value) => {
              if (settled) return;
              settled = true;
              clearTimeout(attempt.timer);
              attempt.cancel = null;
              callback(value);
            };
            attempt.cancel = () => finish(reject, new Error('Add result is no longer current'));
            attempt.timer = setTimeout(() => {
              finish(reject, new Error('Add result could not be confirmed within 12 seconds'));
              try { attempt.controller?.abort(); } catch (_) {}
            }, 12000);
            Promise.resolve().then(() => {
              if (!this.ofkIsCurrent(attempt)) throw new Error('Add result is no longer current');
              attempt.sent = true;
              return fetch(`${routes.cart_add_url}`, config);
            }).then(async (res) => {
              const body = await res.json();
              if (res.status === 429 || res.status >= 500 || !body || typeof body !== 'object') {
                throw new Error('The add response could not be confirmed');
              }
              // Only explicit Shopify validation errors follow the existing correction flow.
              if (body.status && ![400, 404, 422].includes(Number(body.status))) {
                throw new Error('The add response could not be confirmed');
              }
              if (!body.status && (res.ok === false || (!body.id && !(Array.isArray(body.items) && body.items.length && body.items.every((item) => item?.id))))) {
                throw new Error('The add response could not be confirmed');
              }
              finish(resolve, body);
            }).catch((error) => finish(reject, error));
          });
          if (!this.ofkIsCurrent(attempt)) return;
          if (attempt.snapshot !== this.ofkFormSnapshot()) throw new Error('The selected item changed while adding');

          if (response.status) {
            this.ofkPublish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form', productVariantId: variantId,
              errors: response.errors || response.description, message: response.message,
            });
            if (!this.ofkIsCurrent(attempt)) return;
            if (attempt.snapshot !== this.ofkFormSnapshot()) throw new Error('The selected item changed while adding');
            this.handleErrorMessage(response.description);
            this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
            attempt.deferred?.reject(new Error(response.description || response.message));
            if (!this.ofkIsCurrent(attempt)) return;
            if (attempt.snapshot !== this.ofkFormSnapshot()) throw new Error('The selected item changed while adding');
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          }

          attempt.confirmed = true;
          this.resolveCartLinesUpdate(attempt.deferred);
          if (!this.ofkIsCurrent(attempt)) return;
          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (!this.error) this.ofkPublish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form', productVariantId: variantId, cartData: response,
          });
          if (!this.ofkIsCurrent(attempt)) return;
          if (attempt.snapshot !== this.ofkFormSnapshot()) throw new Error('The selected item changed while adding');
          this.error = false;
          const render = () => {
            try {
              if (!this.ofkIsCurrent(attempt)) return;
              if (attempt.snapshot !== this.ofkFormSnapshot()) {
                this.ofkUnknown(attempt);
                return;
              }
              try { this.ofkMeasureRender(() => this.cart.renderContents(response)); }
              catch (_) { this.ofkUnknown(attempt); }
            } finally { this.ofkFinishHandoff(attempt); }
          };
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            attempt.handoff = true;
            attempt.handoffTimer = setTimeout(() => { if (this.ofkAttempt === attempt) this.ofkInvalidateAttempt(); }, 12000);
            attempt.modalClosed = () => {
              attempt.modalClosed = null;
              attempt.renderTimer = setTimeout(render, 0);
            };
            document.body.addEventListener('modalClosed', attempt.modalClosed, { once: true });
            quickAddModal.hide(true);
          } else render();
        } catch (error) {
          if (this.ofkIsCurrent(attempt)) this.ofkUnknown(attempt);
          attempt.deferred?.reject(error);
        } finally {
          if (this.ofkAttempt === attempt) {
            attempt.pending = false;
            if (!attempt.invalid && this.ofkPageActive) {
              this.ofkFinishLoading();
              try { CartPerformance.measureFromEvent('add:user-action', evt); } catch (_) {}
            }
          }
        }
      }

      ofkFormSnapshot() {
        return JSON.stringify(Array.from(new FormData(this.form).entries(), ([name, value]) =>
          [name, typeof value === 'string' ? value : [value.name, value.size, value.type, value.lastModified]]));
      }

      ofkIsCurrent(attempt) {
        return this.ofkAttempt === attempt && !attempt.invalid && this.ofkPageActive &&
          (this.isConnected || (attempt.confirmed && attempt.handoff));
      }

      ofkInvalidateAttempt() {
        const attempt = this.ofkAttempt;
        if (!attempt) return;
        attempt.invalid = true;
        if (attempt.pending && attempt.sent) this.ofkNeedsReview = true;
        attempt.cancel?.();
        try { attempt.controller?.abort(); } catch (_) {}
        clearTimeout(attempt.renderTimer);
        if (attempt.modalClosed) document.body.removeEventListener('modalClosed', attempt.modalClosed);
        this.ofkFinishHandoff(attempt);
        attempt.deferred?.reject(new Error('Add observation is no longer current'));
        attempt.pending = false;
        this.ofkFinishLoading();
      }

      ofkFinishHandoff(attempt) {
        clearTimeout(attempt.handoffTimer);
        attempt.handoff = false;
        if (!this.isConnected) {
          window.removeEventListener('pagehide', this.ofkOnPageHide);
          window.removeEventListener('pageshow', this.ofkOnPageShow);
        }
      }

      ofkFinishLoading() {
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
        if (this.ofkAttempt?.confirmed && this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      }

      ofkPublish(event, data) {
        try { Promise.resolve(publish(event, data)).catch(() => {}); } catch (_) {}
      }

      ofkMeasureRender(render) {
        let started = false;
        let renderError;
        try {
          CartPerformance.measure('add:paint-updated-sections', () => {
            started = true;
            try { render(); } catch (error) { renderError = error; throw error; }
          });
        } catch (_) {
          if (renderError) throw renderError;
          if (!started) render();
        }
      }

      ofkUnknown(attempt) {
        if (!this.ofkIsCurrent(attempt)) return;
        this.ofkNeedsReview = true;
        this.ofkShowRecovery();
        this.dispatchCartErrorEvent('Please check your cart before adding again.', 'SERVICE_UNAVAILABLE');
        attempt.deferred?.reject(new Error('Please check your cart before adding again.'));
      }

      ofkCartRecoveryUrl() {
        try {
          const url = new URL(window.routes.cart_url, window.location.origin);
          if (url.origin !== window.location.origin || url.username || url.password || url.hash ||
              !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/.test(url.pathname)) return null;
          url.search = '';
          url.searchParams.set('view', 'ofk-native');
          return url.href;
        } catch (_) { return null; }
      }

      ofkShowRecovery() {
        if (!this.ofkRecovery) {
          const region = document.createElement('div');
          region.className = 'product-form__error-message-wrapper';
          region.setAttribute('role', 'alert');
          region.setAttribute('data-ofk-add-recovery', '');
          const message = document.createElement('p');
          message.textContent = 'We could not confirm the final cart state. The item may already be in your cart. Please check its items and quantities before adding again.';
          region.appendChild(message);
          const href = this.ofkCartRecoveryUrl();
          if (href) {
            const link = document.createElement('a');
            link.href = href;
            link.textContent = 'Check my cart';
            region.appendChild(link);
          } else {
            const hint = document.createElement('p');
            hint.textContent = 'Open the cart using this store’s cart menu.';
            region.appendChild(hint);
          }
          const confirm = document.createElement('button');
          confirm.type = 'button';
          confirm.className = 'button button--secondary';
          confirm.textContent = 'I checked my cart — allow another add';
          confirm.addEventListener('click', () => {
            this.ofkNeedsReview = false;
            region.hidden = true;
          });
          region.appendChild(confirm);
          this.ofkRecoveryConfirm = confirm;
          this.appendChild(region);
          this.ofkRecovery = region;
        }
        this.ofkRecovery.hidden = false;
        this.ofkRecoveryConfirm.hidden = !this.isConnected;
        if (!this.isConnected) {
          // Quick-add has already removed its form; keep the explicit cart link visible.
          (document.querySelector('main') || document.body).appendChild(this.ofkRecovery);
          this.ofkRecovery.setAttribute('tabindex', '-1');
          try {
            this.ofkRecovery.focus();
            this.ofkRecovery.scrollIntoView({ block: 'center' });
          } catch (_) {}
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        let deferred;
        try {
          const { CartLinesUpdateEvent } = window.StandardEvents || {};
          if (!CartLinesUpdateEvent) return null;
          const raw = CartLinesUpdateEvent.createPromise();
          // Observers may be absent; retain the original promise contract without an unhandled rejection.
          raw.promise.catch(() => {});
          let settled = false;
          let observerTimer;
          const onPageHide = () => deferred.reject(new Error('Cart observation left the page'));
          const clean = () => {
            clearTimeout(observerTimer);
            window.removeEventListener('pagehide', onPageHide);
          };
          deferred = {
            promise: raw.promise,
            get settled() { return settled; },
            resolve(value) { if (!settled) { settled = true; clean(); try { raw.resolve(value); } catch (_) {} } },
            reject(error) { if (!settled) { settled = true; clean(); try { raw.reject(error); } catch (_) {} } },
          };
          // Optional cart-data observers must not remain unresolved or settle after navigation.
          window.addEventListener('pagehide', onPageHide, { once: true });
          observerTimer = setTimeout(() => deferred.reject(new Error('Cart observation timed out')), 12000);
          this.dispatchEvent(new CartLinesUpdateEvent({
            action: 'add', context: 'product',
            lines: [{ merchandiseId: variantId, quantity }], promise: deferred.promise,
          }));
          return deferred;
        } catch (error) {
          deferred?.reject(error);
          return null;
        }
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        try {
          const { CartLinesUpdateEvent } = window.StandardEvents || {};
          if (!CartLinesUpdateEvent) return;
          const pendingCartDataPromise = typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());
          Promise.resolve(pendingCartDataPromise).then((cart) => {
            if (deferred.settled) return;
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          }).catch((error) => deferred.reject(error));
        } catch (error) { deferred.reject(error); }
      }

      dispatchCartErrorEvent(message, code) {
        try {
          const { CartErrorEvent } = window.StandardEvents || {};
          if (CartErrorEvent) this.dispatchEvent(new CartErrorEvent({ error: message, code }));
        } catch (_) { /* An optional observer cannot hold the commercial button. */ }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
